import { useUserData } from '@shared/hooks/useUser';
import { useAuthStore } from '@shared/stores/authStore';

/**
 * Hook para obtener la moneda del usuario actual desde los datos de /me (authStore o useUserData)
 * @returns La moneda del usuario (ej: 'EUR', 'MXN', 'USD')
 */
export const useCurrency = (): string => {
  const { data: userData } = useUserData();
  const authSettings = useAuthStore((state) => state.settings);
  const team = useAuthStore((state) => state.team);

  // Intentamos obtener la moneda en varios niveles de profundidad
  const currencyFromUser =
    userData?.settings?.currency?.value?.value ?? userData?.settings?.currency?.value ?? userData?.settings?.currency;

  const currencyFromAuth =
    authSettings?.['currency']?.value?.value ?? authSettings?.['currency']?.value ?? authSettings?.['currency'];

  const currency = currencyFromUser ?? currencyFromAuth ?? (team as any)?.currency ?? 'EUR';

  return typeof currency === 'string' ? currency : 'EUR';
};

// Locale nativo de cada moneda — usado para obtener el símbolo correcto y la posición (antes/después)
const CURRENCY_NATIVE_LOCALE: Record<string, string> = {
  MXN: 'es-MX',
  USD: 'en-US',
  CAD: 'en-CA',
  AUD: 'en-AU',
  GBP: 'en-GB',
  BRL: 'pt-BR',
  COP: 'es-CO',
  CLP: 'es-CL',
  ARS: 'es-AR',
  PEN: 'es-PE',
  JPY: 'ja-JP',
  CNY: 'zh-CN',
};

export const getCurrencySymbol = (currencyCode: string, locale = 'es-ES'): string => {
  const code = currencyCode || 'EUR';
  const referenceLocale = CURRENCY_NATIVE_LOCALE[code] ?? locale;
  const extractSymbol = (loc: string) =>
    new Intl.NumberFormat(loc, {
      style: 'currency',
      currency: code,
      currencyDisplay: 'narrowSymbol',
    })
      .formatToParts(0)
      .find((p) => p.type === 'currency')?.value ?? code;

  try {
    const symbol = extractSymbol(referenceLocale);
    return symbol !== code ? symbol : extractSymbol('en-US');
  } catch {
    return code;
  }
};

export const formatCurrencyAmount = (value: number, currencyCode: string, locale = 'es-ES'): string => {
  const code = currencyCode || 'EUR';
  try {
    const referenceLocale = CURRENCY_NATIVE_LOCALE[code] ?? locale;
    const symbol = getCurrencySymbol(code);

    // Determina si el símbolo va antes o después usando el locale nativo de la moneda
    const referenceParts = new Intl.NumberFormat(referenceLocale, {
      style: 'currency',
      currency: code,
      currencyDisplay: 'narrowSymbol',
    }).formatToParts(1);

    const symbolBefore =
      referenceParts.findIndex((p) => p.type === 'currency') < referenceParts.findIndex((p) => p.type === 'integer');

    // Formatea el número con separadores españoles (coma decimal, punto miles)
    const formattedNumber = new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);

    return symbolBefore ? `${symbol}${formattedNumber}` : `${formattedNumber} ${symbol}`;
  } catch {
    return `${value.toFixed(2)} ${getCurrencySymbol(code)}`;
  }
};

/**
 * Hook para obtener el símbolo de la moneda del usuario actual
 * @returns El símbolo de la moneda (ej: '€', '$', 'MXN')
 */
export const useCurrencySymbol = (): string => {
  const currency = useCurrency();
  return getCurrencySymbol(currency);
};
